import { ReactNode } from 'react';

import {
  TTransaction,
  TTransactionOrigin,
  TTransactionPaymentMethod,
  TTransactionType,
  TTransactionTableColumns,
} from '@entities/transactions';
import { ITransactionDepositTableColumns } from '@entities/transactions/deposit';
import { ITransactionWithdrawalTableColumns } from '@entities/transactions/withdrawal';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import RefreshBtn from '@features/refresh-btn';
import {
  FilterControlBlock,
  FilterDateRange,
  FilterInput,
  FilterSelect,
} from '@features/table-filter';
import { Tooltip } from '@mui/material';
import RequestStatus from '@shared/components/RequestStatusLabel';
import { formatAmount } from '@shared/utils/formatAmount';

interface IProps {
  type: TTransactionType;
  refetch?: () => void;
}

export const useTableSettings = ({ type, refetch }: IProps) => {
  // THead settings
  const depositColumnTitles: ITransactionDepositTableColumns = {
    id: 'Transaction ID',
    order_id: 'Order ID',
    timestamp: 'Transaction Date',
    wallet: 'Wallet',
    bank_name: 'Bank Name',
    utr: 'UTR',
    amount: 'Amount',
    status: 'Status from bank',
    external_reference: 'External Reference',
    customer_name: 'Customer Name',
    customer_phone: 'Mobile',
    bank_balance: 'Balance',
    origin: 'Origin',
    matched: 'Matched',
    human_hash: 'Short ID',
    upi_id: 'UPI ID',
    created_at: 'Upload Date',
    method: 'Payment Method',
  };

  const withdrawalColumnTitles: ITransactionWithdrawalTableColumns = {
    id: 'Transaction ID',
    order_id: 'Order ID',
    timestamp: 'Transaction Date',
    wallet: 'Wallet',
    bank_name: 'Bank Name',
    utr: 'UTR',
    amount: 'Amount',
    status: 'Status from bank',
    external_reference: 'External Reference',
    customer_name: 'Customer Name',
    customer_phone: 'Mobile',
    bank_balance: 'Balance',
    origin: 'Origin',
    matched: 'Matched',
    human_hash: 'Short ID',
    upi_id: 'UPI ID',
    created_at: 'Upload Date',
    method: 'Payment Method',
  };

  const getColumnTitles = (type: TTransactionType) => {
    switch (type) {
      case 'deposit':
        return depositColumnTitles;
      case 'withdrawal':
        return withdrawalColumnTitles;
    }
  };

  const filterStatuses = ['pending', 'new', 'success', 'failed'];
  if (type === 'withdrawal') filterStatuses.push('refund');
  const filterStatusesOptions = filterStatuses.map(status => ({
    id: status,
    label: status,
  }));

  const filterMatchedOptions = [
    { id: 'true', label: 'Yes' },
    { id: 'false', label: 'No' },
  ];

  const filterOrigins: TTransactionOrigin[] = [
    'sms',
    'parser',
    'manual-upload',
    'google-upload',
    'direct-upload',
  ];
  if (type === 'deposit')
    filterOrigins.push('upi-intent', 'push-notification', 'trader', 'auto-expiration');
  if (type === 'withdrawal') filterOrigins.push('withdrawal-api', 'upload');

  const filterOriginsOptions = filterOrigins.map(origin => ({
    id: origin,
    label: origin,
  }));

  const filterPaymentMethods: TTransactionPaymentMethod[] = ['UPI', 'IMPS', 'RTGS', 'NEFT'];
  const filterPaymentMethodsOptions = filterPaymentMethods.map(method => ({
    id: method,
    label: method,
  }));

  const renderFilterElement = (key: keyof TTransaction) => {
    switch (key) {
      case 'method':
        return <FilterSelect name={key} options={filterPaymentMethodsOptions} />;
      case 'status':
        return <FilterSelect name={key} options={filterStatusesOptions} />;
      case 'matched':
        return <FilterSelect name={key} options={filterMatchedOptions} />;
      case 'origin':
        return <FilterSelect name={key} options={filterOriginsOptions} />;
      case 'created_at':
      case 'timestamp':
        return <FilterDateRange name={key} />;

      default:
        return <FilterInput name={key} />;
    }
  };

  const getTheadBefore = (): ReactNode => {
    return null;
  };
  const getTheadAfter = () => (
    <th>
      <FilterControlBlock storageKey={`transaction-${type}`}>
        <Tooltip title='Refresh'>
          <span>
            <RefreshBtn refetch={refetch} withText={false} />
          </span>
        </Tooltip>
      </FilterControlBlock>
    </th>
  );

  // TBody settings
  const renderRow = (key: keyof TTransactionTableColumns, transaction: TTransaction) => {
    switch (key) {
      case 'amount':
        return transaction[key] && `${formatAmount(String(transaction[key]))}`;
      case 'matched':
        return transaction[key] ? 'Yes' : 'No';
      case 'status':
        return <RequestStatus status={transaction[key]} />;
      case 'utr':
      case 'human_hash':
      case 'id':
        return <ReduceAndCopyString text={String(transaction[key])} />;
      case 'order_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/orders/${type}?filter[id]=${transaction[key]}`}
            text={String(transaction[key])}
          />
        );
      case 'customer_phone':
        return transaction.customer_phone;
      default:
        return transaction[key];
    }
  };

  const getTbodyBefore = (transaction: TTransaction): ReactNode => {
    return null;
  };
  const getTbodyAfter = (transaction: TTransaction): ReactNode => {
    return <td></td>;
  };

  return {
    columnTitles: getColumnTitles(type),

    renderFilterElement,
    getTheadBefore,
    getTheadAfter,

    renderRow,
    getTbodyBefore,
    getTbodyAfter,
  };
};
